/*
  Delivery note from a photo (UI overhaul wave 3, screen 05 idea 10).

  1. Take a photo of the paper DN (camera on phones) or pick one — it is sent to
     POST /projects/{id}/delivery-notes/read-photo, where the AI model reads the DN number, date, supplier and
     each line, and the server links every line to the BOQ line with the same part number.
  2. The person checks every line: which BOQ line it is, the quantity, and whether to use it. Lines the server
     could not link start unticked and need a BOQ line picked.
  3. "Fill the delivery note" opens the usual Add Delivery Note form with these quantities — nothing is saved
     until the person saves that form.
  OFF by default: the photo goes to Google, so it needs the `ai_extras` feature (Admin › Integrations, off until an
  admin switches it on) AND the server switch AI_PROJECT_FEATURES_ENABLED (useProjectAi). While either is off the
  panel says so (NotConfigured / ProjectAiSwitchedOff) and offers no upload — the photo is never sent anywhere.
*/
import { useEffect, useMemo, useState } from 'react';
import { Camera, CircleAlert, ScanText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { AI_EXTRAS, FEATURE_STATUS, aiExtrasHelp } from '@/lib/integrations';
import { uploadFile } from '@/lib/upload';
import { Input } from '@/components/ui/input';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { StatusPill } from '@/components/ui/status-pill';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { NotConfigured } from '@/components/ui/not-configured';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MONO } from './ProjectUi';
import { parseQtyInput } from './materialModel';
import { ProjectAiSwitchedOff, useProjectAi } from './useProjectAi';

const NONE = '__none__';
const MATCH_LABEL = {
  exact: { tone: 'green', label: 'Matched' },
  similar: { tone: 'blue', label: 'Similar part no.' },
  ambiguous: { tone: 'amber', label: 'Pick the line' },
  none: { tone: 'amber', label: 'Not in BOQ' },
};

/** The reviewed lines → { [boqItemId]: qty } for the delivery-note form (same BOQ line twice adds up). */
export function dnLinesToQuantities(lines = []) {
  const result = {};
  lines.forEach((line) => {
    if (!line.use || !line.boqItemId) return;
    const parsed = parseQtyInput(line.qty);
    if (parsed.error || !(parsed.value > 0)) return;
    result[line.boqItemId] = (result[line.boqItemId] || 0) + parsed.value;
  });
  return result;
}

/**
 * The server's lines as editable rows. `boqItems` are the lines the delivery-note form shows (on a Sub BOQ
 * project: the Main BOQ rows) — a line the server linked to a BOQ item that is not one of them is re-linked by
 * its part number.
 */
export function toDraftLines(lines = [], boqItems = []) {
  const ids = new Set(boqItems.map((item) => item.id));
  const byPart = new Map();
  boqItems.forEach((item) => {
    const part = String(item.part_number || '').trim().toUpperCase();
    if (part && !byPart.has(part)) byPart.set(part, item.id);
  });
  return lines.map((line, index) => {
    let boqItemId = line.boq_item_id || '';
    if (boqItemId && !ids.has(boqItemId)) boqItemId = byPart.get(String(line.boq_part_number || line.part_number || '').trim().toUpperCase()) || '';
    return {
      key: `${index}-${line.part_number}`,
      part: line.part_number || '',
      description: line.description || '',
      match: boqItemId ? (line.match || 'none') : (line.match === 'ambiguous' ? 'ambiguous' : 'none'),
      boqItemId,
      qty: line.qty === null || line.qty === undefined ? '' : String(line.qty),
      use: Boolean(boqItemId) && Number(line.qty) > 0,
    };
  });
}

export function DnPhotoPanel({ open, onOpenChange, projectId, boqItems = [], onConfirm }) {
  const projectAi = useProjectAi();
  const { extras } = projectAi;
  const [reading, setReading] = useState(null);
  const [header, setHeader] = useState({ dn_number: '', delivery_date: '', supplier: '' });
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (!open) return;
    setReading(null);
    setHeader({ dn_number: '', delivery_date: '', supplier: '' });
    setLines([]);
  }, [open]);

  const boqOptions = useMemo(() => boqItems
    .filter((item) => item?.id)
    .map((item) => ({ id: item.id, label: `${item.part_number || '—'}${item.sub_boq ? ` · ${item.sub_boq}` : ''}`, description: item.description || '' })), [boqItems]);
  const quantities = dnLinesToQuantities(lines);
  const usedCount = Object.keys(quantities).length;
  const invalid = lines.filter((line) => line.use && (!line.boqItemId || parseQtyInput(line.qty).error || !(parseQtyInput(line.qty).value > 0)));
  const setLine = (key, change) => setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...change } : line)));

  const save = async () => {
    if (!reading) throw new Error('Take or choose a photo of the delivery note first');
    if (invalid.length) throw new Error(`Check ${invalid.length} line${invalid.length === 1 ? '' : 's'}: pick a BOQ line and a quantity above 0, or untick it`);
    if (!usedCount) throw new Error('Tick at least one line to use');
    onConfirm?.({ ...header, line_qty: quantities });
  };

  // AI extras not set up, or not switched on by an admin (the default)
  const aiOff = !extras.loading && !extras.enabled;
  // AI extras is on, but the server switch for the project page's AI is not (the default)
  const switchedOff = extras.enabled && !projectAi.loading && !projectAi.enabled;
  const checking = !aiOff && !switchedOff && !projectAi.enabled;
  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      icon={ScanText}
      title="Delivery note from photo"
      subtitle="The AI reads the lines — you check them before anything is saved"
      dirty={Boolean(reading)}
      onSave={reading ? save : undefined}
      saveLabel={usedCount ? `Fill the delivery note (${usedCount} line${usedCount === 1 ? '' : 's'})` : 'Fill the delivery note'}
      saveDisabled={!reading}
      confirmOptions={{ title: 'Discard the lines read from the photo?', confirmLabel: 'Discard', cancelLabel: 'Keep checking' }}
      testId="dn-photo-panel"
    >
      {aiOff ? (
        <NotConfigured
          feature={AI_EXTRAS}
          title={extras.status === FEATURE_STATUS.OFF ? 'Reading delivery notes from photos is switched off' : 'Reading delivery notes needs the AI assistant'}
          text={aiExtrasHelp(extras, 'Type the quantities in New › Delivery note, or paste them from Excel there.')}
          testId="dn-photo-not-configured"
        />
      ) : switchedOff ? (
        <ProjectAiSwitchedOff
          title="Reading delivery notes from photos is switched off"
          text="Type the quantities in New › Delivery note, or paste them from Excel there."
          testId="dn-photo-switched-off"
        />
      ) : checking ? (
        <span className="ui-skeleton-shimmer block h-32 rounded-[12px]" role="status" aria-label="Checking whether photo reading is on" />
      ) : (
        <>
          <SidePanelSection title="1 · Photo">
            <FileDropzone
              accept="image/*"
              multiple={false}
              capture="environment"
              maxSize={8 * 1024 * 1024}
              label="Take a photo of the delivery note or drop it here"
              hint="JPG, PNG or HEIC up to 8 MB · one page at a time"
              upload={(file, { onProgress, signal }) => uploadFile(`/projects/${projectId}/delivery-notes/read-photo`, file, { field: 'photo', onProgress, signal })}
              onUploaded={(result) => {
                setReading(result || null);
                setHeader({ dn_number: result?.dn_number || '', delivery_date: result?.delivery_date || '', supplier: result?.supplier || '' });
                setLines(toDraftLines(result?.lines || [], boqItems));
              }}
              testId="dn-photo-drop"
            />
          </SidePanelSection>
          {reading ? (
            <>
              <SidePanelSection title="2 · Delivery note" description="Read from the photo — correct anything that is wrong.">
                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="block space-y-1">
                    <span className="text-[12px] font-semibold text-ink-2">DN number</span>
                    <Input value={header.dn_number} onChange={(event) => setHeader((prev) => ({ ...prev, dn_number: event.target.value }))} className={MONO} aria-label="DN number" />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-[12px] font-semibold text-ink-2">Date</span>
                    <Input type="date" value={header.delivery_date} onChange={(event) => setHeader((prev) => ({ ...prev, delivery_date: event.target.value }))} aria-label="Delivery date" />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-[12px] font-semibold text-ink-2">Supplier</span>
                    <Input value={header.supplier} onChange={(event) => setHeader((prev) => ({ ...prev, supplier: event.target.value }))} aria-label="Supplier" />
                  </label>
                </div>
                {reading.existing_delivery_note ? (
                  <p className="mt-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-status-amber-ink" role="note">
                    <CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />
                    This project already has a delivery note <span className={MONO}>{reading.existing_delivery_note.dn_number}</span> — check it is not the same one.
                  </p>
                ) : null}
              </SidePanelSection>
              <SidePanelSection
                title={`3 · Lines · ${lines.length} read · ${reading.matched || 0} matched`}
                description="Ticked lines go into the delivery note. Pick the BOQ line for anything the system could not match."
              >
                {lines.length ? (
                  <div className="overflow-x-auto rounded-lg border border-line">
                    <table className="w-full min-w-[560px] border-separate border-spacing-0 text-[13px]" data-testid="dn-photo-lines">
                      <thead>
                        <tr className="text-left text-[11.5px] font-bold text-ink-3">
                          <th className="w-9 border-b border-line bg-surface-2 px-2 py-1.5"><span className="sr-only">Use</span></th>
                          <th className="border-b border-line bg-surface-2 px-2 py-1.5">Read on the DN</th>
                          <th className="border-b border-line bg-surface-2 px-2 py-1.5">BOQ line</th>
                          <th className="w-[84px] border-b border-line bg-surface-2 px-2 py-1.5 text-right">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((line, index) => {
                          const meta = MATCH_LABEL[line.match] || MATCH_LABEL.none;
                          const qtyError = line.use ? parseQtyInput(line.qty).error : '';
                          return (
                            <tr key={line.key} className={cn(!line.use && 'text-ink-3')} data-testid={`dn-photo-line-${index}`}>
                              <td className="border-b border-line-2 px-2 py-1.5">
                                <TableCheckbox checked={line.use} onCheckedChange={(checked) => setLine(line.key, { use: Boolean(checked) })} aria-label={`Use line ${index + 1}`} />
                              </td>
                              <td className="border-b border-line-2 px-2 py-1.5">
                                <span className={cn(MONO, 'block text-[12.5px] font-semibold text-ink')}>{line.part || '—'}</span>
                                {line.description ? <span className="block max-w-[220px] truncate text-[12px] text-ink-3" title={line.description}>{line.description}</span> : null}
                                {!line.boqItemId || line.match !== 'exact' ? <StatusPill tone={line.boqItemId ? 'blue' : meta.tone} size="sm" label={line.boqItemId && line.match === 'none' ? 'Picked' : meta.label} className="mt-1" /> : null}
                              </td>
                              <td className="border-b border-line-2 px-2 py-1.5">
                                <Select value={line.boqItemId || NONE} onValueChange={(value) => setLine(line.key, { boqItemId: value === NONE ? '' : value, use: value === NONE ? false : true })}>
                                  <SelectTrigger className="h-8 min-w-[170px] text-[12.5px]" aria-label={`BOQ line for ${line.part || `line ${index + 1}`}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={NONE}>Not in the BOQ</SelectItem>
                                    {boqOptions.map((option) => <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="border-b border-line-2 px-2 py-1.5 text-right">
                                <Input
                                  value={line.qty}
                                  onChange={(event) => setLine(line.key, { qty: event.target.value })}
                                  inputMode="decimal"
                                  className={cn('h-8 w-[72px] text-right tabular-nums', qtyError && 'border-status-red-ink')}
                                  aria-label={`Quantity of ${line.part || `line ${index + 1}`}`}
                                  aria-invalid={qtyError ? true : undefined}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-line px-3 py-3 text-[12.5px] text-ink-3">
                    No lines could be read. Try a sharper photo of the whole page, or type the quantities in New › Delivery note.
                  </p>
                )}
                {usedCount ? (
                  <p className="mt-2 text-[12.5px] text-ink-3" role="status">
                    {usedCount} BOQ line{usedCount === 1 ? '' : 's'} · {formatNumber(Object.values(quantities).reduce((sum, value) => sum + value, 0), { maxDecimals: 2 })} pcs will be filled in.
                  </p>
                ) : null}
              </SidePanelSection>
            </>
          ) : (
            <p className="flex items-center gap-2 px-1 text-[12.5px] text-ink-3">
              <Camera className="h-3.5 w-3.5" aria-hidden="true" />
              On a phone the camera opens; hold it flat over the whole page.
            </p>
          )}
        </>
      )}
    </SidePanel>
  );
}
